/**
 * PathMigrationService - relay 路径无缝迁移（direct ⇄ forward）
 *
 * 职责：pm 备用连接全生命周期（docs/path_observer_design.md §4.2）。
 * make-before-break：后台建好新 relay 连接（ICE → prepare → switch_ready → 视频轨）
 * 后经 WebRTCService.adoptConnection 转正，再发 switch 让 host 关旧主 session。
 *
 * 信令：控制消息走 `path_migrate` 消息族（模仿 p2p_assist）；SDP/ICE 复用现有
 * webrtcviewer+raceId 通道（raceId = pm sessionId），由 WebRTCService 按 'pm-' 前缀
 * 分流到 handleOffer/handleCandidate，绝不进 IceRacingService。
 */

import { eventBus } from './EventBus';
import { signalingService } from './SignalingService';
import { iceRacingService } from './IceRacingService';
import { webRTCService } from './WebRTCService';

export type MigrationTargetPath = 'direct' | 'forward';
type DoneCallback = (ok: boolean, reason?: string) => void;

// TURN 凭证/端口与 IceRacingService 的 TURN_SERVER_* 常量保持一致
// （那边是模块私有常量无法 import，改凭证/端口时两处需同步）
const TURN_USERNAME = 'hudd';
const TURN_CREDENTIAL = 'huddhudd';
const TURN_PORT = 3400;
// lastWinnerTurnHost 不可得时回退 race 配置第一个 TURN（TURN_SERVER_UDP 的主机）
const FALLBACK_TURN_HOST = 'rtc.03kan.com';

// 每步超时（§4.2 流程 4）。ICE 超时从 start 起算，host 不回 offer 也归入 ice_timeout
const ICE_CONNECT_TIMEOUT_MS = 8000;
const SWITCH_READY_TIMEOUT_MS = 5000;
const VIDEO_TRACK_TIMEOUT_MS = 5000;

class PathMigrationService {
  private _log(...args: unknown[]) { console.log('[PathMigration]', ...args); }
  private _warn(...args: unknown[]) { console.warn('[PathMigration]', ...args); }

  private sessionId: string | null = null;
  private targetPath: MigrationTargetPath = 'direct';
  private pc: RTCPeerConnection | null = null;
  private onDone: DoneCallback | null = null;
  private turnUrls: string[] = [];
  private startTs = 0;
  private prepareSentTs = 0;

  // 通道铁律（memory/project_channel_architecture.md）：
  //   input 必须浏览器创建（host OnDataChannel 用它替换 data_channel_）；
  //   mouse 不创建，用 host 在 AddTracks 创建的（ondatachannel 接收）—— 照 P2PAssist 辅助 PC 做法
  private inputChannel: RTCDataChannel | null = null;
  private mouseChannel: RTCDataChannel | null = null;
  private hostDataChannel: RTCDataChannel | null = null; // host 的 'data' 通道（仅记录，主控制通道是 input）

  private remoteStream: MediaStream | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = []; // 早于 setRemoteDescription 到达的 host candidate
  private candidateCount = 0;
  private iceConnected = false;
  private switchReady = false;
  private videoReceived = false;
  private finished = false; // 终态守卫：成功/失败/中止后所有回调全部短路

  private iceTimer: number | null = null;
  // 两阶段共用：阶段1 等 switch_ready（5s）；阶段2 等视频轨+input open（5s）
  private readyTimer: number | null = null;

  constructor() {
    eventBus.on('path_migrate:switch_ready', (d) => this.handleSwitchReady(d));
    eventBus.on('path_migrate:error', (d) => this.handleHostError(d));
  }

  get active(): boolean { return this.sessionId !== null; }
  get activeSessionId(): string | null { return this.sessionId; }

  /**
   * 启动迁移。返回 pm sessionId；返回 null 表示未能启动（已同步回调 onDone(false)）。
   * 每步失败 → cleanup + onDone(false, step)。
   */
  startMigration(targetPath: MigrationTargetPath, onDone: DoneCallback): string | null {
    if (this.sessionId) {
      this._warn(`startMigration rejected: migration in flight (${this.sessionId})`);
      onDone(false, 'busy');
      return null;
    }

    // TURN 取当前主连接 winner 同款（凭证与 IceRacingService 配置一致）
    const turnHost = iceRacingService.lastWinnerTurnHost || FALLBACK_TURN_HOST;
    const sessionId = `pm-${targetPath === 'forward' ? 'fwd' : 'dir'}-${Date.now()}`;
    const iceServers: RTCIceServer[] = [
      { urls: `turn:${turnHost}:${TURN_PORT}?transport=udp`, username: TURN_USERNAME, credential: TURN_CREDENTIAL },
      { urls: `turn:${turnHost}:${TURN_PORT}?transport=tcp`, username: TURN_USERNAME, credential: TURN_CREDENTIAL },
    ];

    this.sessionId = sessionId;
    this.targetPath = targetPath;
    this.onDone = onDone;
    this.turnUrls = iceServers.map((s) => String(s.urls));
    this.startTs = Date.now();
    this.prepareSentTs = 0;
    this.pendingCandidates = [];
    this.candidateCount = 0;
    this.iceConnected = false;
    this.switchReady = false;
    this.videoReceived = false;
    this.finished = false;
    this.mouseChannel = null;
    this.hostDataChannel = null;

    this._log(`start: target=${targetPath} session=${sessionId} turn=${turnHost}`);

    try {
      const pc = new RTCPeerConnection({
        iceServers,
        iceTransportPolicy: 'relay',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      });
      this.pc = pc;

      this.inputChannel = pc.createDataChannel('input', { ordered: true, priority: 'high' });
      this.inputChannel.onopen = () => { this.maybeFinalize(); };

      this.remoteStream = new MediaStream();

      pc.ondatachannel = (event) => {
        const ch = event.channel;
        if (ch.label === 'mouse') {
          this.mouseChannel = ch;
        } else if (ch.label === 'data') {
          this.hostDataChannel = ch;
        }
      };

      pc.ontrack = (event) => {
        if (this.remoteStream && !this.remoteStream.getTrackById(event.track.id)) {
          this.remoteStream.addTrack(event.track);
        }
        if (event.track.kind === 'video' && !this.videoReceived) {
          this.videoReceived = true;
          this._log(`video track received: ${this.sessionId}`);
          this.maybeFinalize();
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && this.sessionId) {
          signalingService.send({
            type: 'webrtcviewer',
            kind: 'candidate',
            raceId: this.sessionId,
            message: event.candidate.toJSON(),
          });
        }
      };

      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        if ((state === 'connected' || state === 'completed') && !this.iceConnected) {
          this.iceConnected = true;
          if (this.iceTimer !== null) { clearTimeout(this.iceTimer); this.iceTimer = null; }
          this._log(`ice connected: ${this.sessionId} elapsed=${Date.now() - this.startTs}ms type=relay`);
          this.sendPrepare();
        } else if (state === 'failed') {
          this.fail('ice_failed');
        }
      };

      this.iceTimer = window.setTimeout(() => {
        this.iceTimer = null;
        this.fail('ice_timeout');
      }, ICE_CONNECT_TIMEOUT_MS);

      // 本地 PC 与 offer/candidate 处理器就绪后再发 start，避免 host offer 极快返回时被忽略。
      // start 会让 host 用 forced_path 建 pm session（offer 经现有 OnOffer 链路自动发出）。
      signalingService.send({
        type: 'path_migrate',
        action: 'start',
        sessionId,
        targetPath,
        iceServers: iceServers.map((s) => ({ urls: String(s.urls), username: s.username, credential: s.credential })),
      });

      return sessionId;
    } catch (e) {
      this._warn('create PeerConnection failed:', e);
      // start 尚未发出，host 侧没有对应 pm session，不发送 stop。
      this.fail('pc_create_failed', false);
      return null;
    }
  }

  /** WebRTCService 按 'pm-' 前缀分流过来的 offer（initial = racing 骨架；renegotiate = prepare 后带视频轨） */
  async handleOffer(raceId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    if (raceId !== this.sessionId || !this.pc || this.finished) {
      this._warn(`offer ignored: raceId=${raceId} active=${this.sessionId ?? 'none'}`);
      return;
    }
    const isInitial = !this.pc.remoteDescription;
    this._log(`offer received: ${raceId} (${isInitial ? 'initial' : 'renegotiate'})`);
    try {
      await this.pc.setRemoteDescription(offer);
      if (this.pendingCandidates.length > 0) {
        for (const c of this.pendingCandidates) {
          try {
            await this.pc.addIceCandidate(c);
            this.candidateCount++;
          } catch (e) {
            this._warn('addIceCandidate(pending) failed:', e);
          }
        }
        this.pendingCandidates = [];
        this._log(`candidate added: count=${this.candidateCount} (pending flushed)`);
      }
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      signalingService.send({ type: 'webrtcviewer', kind: 'answer', raceId, message: answer });
      this._log(`answer sent: ${raceId} (${isInitial ? 'initial' : 'renegotiate'})`);
    } catch (e) {
      this._warn('offer handling failed:', e);
      this.fail('sdp_failed');
    }
  }

  /** WebRTCService 按 'pm-' 前缀分流过来的 host candidate */
  async handleCandidate(raceId: string, candidate: RTCIceCandidateInit): Promise<void> {
    if (raceId !== this.sessionId || !this.pc || this.finished) return;
    if (!this.pc.remoteDescription) {
      this.pendingCandidates.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(candidate);
      this.candidateCount++;
      this._log(`candidate added: count=${this.candidateCount}`);
    } catch (e) {
      this._warn('addIceCandidate failed:', e);
    }
  }

  /** 中止迁移（p2p 抢占 / 主连接断开复位）。不回调 onDone —— 调用方自行处理状态 */
  abort(reason: string): void {
    if (!this.sessionId) return;
    this._log(`abort: session=${this.sessionId} reason=${reason}`);
    signalingService.send({ type: 'path_migrate', action: 'stop', sessionId: this.sessionId });
    this.finished = true;
    this.clearState(true);
  }

  // ===== 内部流程 =====

  private sendPrepare(): void {
    if (!this.sessionId || this.finished) return;
    this.prepareSentTs = Date.now();
    signalingService.send({ type: 'path_migrate', action: 'prepare', sessionId: this.sessionId });
    this._log(`prepare sent: ${this.sessionId}`);
    this.readyTimer = window.setTimeout(() => {
      this.readyTimer = null;
      this.fail('prepare_timeout');
    }, SWITCH_READY_TIMEOUT_MS);
  }

  private handleSwitchReady(d: { sessionId: string }): void {
    if (d.sessionId !== this.sessionId || this.finished) return;
    this.switchReady = true;
    if (this.readyTimer !== null) { clearTimeout(this.readyTimer); this.readyTimer = null; }
    const elapsed = this.prepareSentTs > 0 ? Date.now() - this.prepareSentTs : 0;
    this._log(`switch_ready received: ${this.sessionId} (elapsed=${elapsed}ms)`);
    if (!this.maybeFinalize()) {
      // 视频轨（重协商 offer 触发 ontrack）或 input 通道未就绪：限时等待
      this.readyTimer = window.setTimeout(() => {
        this.readyTimer = null;
        this.fail(this.videoReceived ? 'input_timeout' : 'track_timeout');
      }, VIDEO_TRACK_TIMEOUT_MS);
    }
  }

  private handleHostError(d: { sessionId: string; reason: string }): void {
    if (d.sessionId !== this.sessionId || this.finished) return;
    // host 已拒绝/清理 session，不再发 stop
    this.fail(`host_error:${d.reason}`, false);
  }

  /** 全就绪门：switch_ready + 视频轨 + input 通道 open（input 是切换后的主控制通道，必须可用） */
  private maybeFinalize(): boolean {
    if (this.finished || !this.sessionId || !this.pc) return false;
    if (!this.switchReady || !this.videoReceived) return false;
    if (this.inputChannel?.readyState !== 'open') return false;
    this.finalize();
    return true;
  }

  private finalize(): void {
    const sessionId = this.sessionId!;
    this.finished = true;
    this.clearTimers();

    this._log('adopting connection (handing to WebRTCService)');
    webRTCService.adoptConnection({
      sessionId,
      pc: this.pc!,
      dataChannel: this.hostDataChannel,
      inputChannel: this.inputChannel,
      mouseChannel: this.mouseChannel,
      remoteStream: this.remoteStream!,
      stunServers: this.turnUrls,
      connectionType: this.targetPath === 'forward' ? 'relay-forward' : 'relay-direct',
    });

    signalingService.send({ type: 'path_migrate', action: 'switch', sessionId });
    this._log('switch signal sent, old session close requested');
    this._log(`DONE: now on ${this.targetPath}, total=${Date.now() - this.startTs}ms`);

    const cb = this.onDone;
    this.clearState(false); // PC/通道已转交主连接，只清引用不能关
    cb?.(true);
  }

  private fail(step: string, sendStop = true): void {
    if (this.finished || !this.sessionId) return;
    this.finished = true;
    this._log(`FAILED: step=${step} session=${this.sessionId}`);
    if (sendStop) {
      signalingService.send({ type: 'path_migrate', action: 'stop', sessionId: this.sessionId });
    }
    const cb = this.onDone;
    this.clearState(true);
    cb?.(false, step);
  }

  private clearTimers(): void {
    if (this.iceTimer !== null) { clearTimeout(this.iceTimer); this.iceTimer = null; }
    if (this.readyTimer !== null) { clearTimeout(this.readyTimer); this.readyTimer = null; }
  }

  private clearState(closePc: boolean): void {
    this.clearTimers();
    if (this.pc && closePc) {
      this.pc.onicecandidate = null;
      this.pc.oniceconnectionstatechange = null;
      this.pc.ontrack = null;
      this.pc.ondatachannel = null;
      if (this.inputChannel) {
        this.inputChannel.onopen = null;
        try { this.inputChannel.close(); } catch { /* ignore */ }
      }
      this.pc.close();
    }
    // closePc=false（成功转交）时不碰通道 handler —— adoptConnection 已接管
    this.pc = null;
    this.inputChannel = null;
    this.mouseChannel = null;
    this.hostDataChannel = null;
    this.remoteStream = null;
    this.pendingCandidates = [];
    this.sessionId = null;
    this.onDone = null;
    this.prepareSentTs = 0;
  }
}

// Singleton instance
export const pathMigrationService = new PathMigrationService();
export default pathMigrationService;
